import React, { PropsWithChildren, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import LayoutContainer from 'components/Common/LayoutContainer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { OFFER_TYPE_HOTEL_SLUG } from 'constants/offer'
import useIsAvailableForPurchase from '../hooks/useIsAvailableForPurchase'
import { NavWrapper } from 'components/OfferPage/styles'
import FormatCurrency from 'components/Common/FormatCurrency'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import {
  getCheapestSelectedPackageOption,
  getCheapestAvailablePackageOption,
  OfferPackageOptionView,
  areCalendarsLoading,
} from 'selectors/offerPage/offerPageSelectors'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import TextButton from 'components/Luxkit/Button/TextButton'
import Caption from 'components/Luxkit/Typography/Caption'
import Heading from 'components/Luxkit/Typography/Heading'
import BodyText from 'components/Luxkit/Typography/BodyText'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import config from 'constants/config'
import { getWeeklyInstalmentAmount, isInstalmentsAvailableOnOffer } from 'lib/payment/payInInstalmentsUtils'
import GeoContext from 'contexts/geoContext'
import SecureWithFlexiblePaymentsPriceDetails from 'components/Common/SecureWithFlexiblePayments/SecureWithFlexiblePaymentsPriceDetails'
import { getDepositAmountPercentage } from 'lib/payment/depositsUtils'
import cx from 'clsx'
import { getInstalmentsAbTestEnabled } from 'selectors/featuresSelectors'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import WalledContent from 'components/Common/WalledContent'
import GenericLoginSignupWall from 'components/Common/WalledContent/GenericLoginSignupWall'
import OfferFromDurationLabel from '../OfferFromDurationLabel'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import Group from 'components/utils/Group'
import ShareModal from 'components/Common/Modals/ShareModal'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import { getShareModalConfig } from 'lib/customer/getShareModalConfig'
import AssistChip from 'components/Luxkit/Chips/AssistChip'
import LineShareAltIcon from 'components/Luxkit/Icons/line/LineShareAltIcon'
import { OptimizelyClientSideExperiments, useOptimizelyExperiment } from 'lib/optimizely/optimizelyUtils'

const Root = styled(LayoutContainer)`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const PriceSummarySection = styled(CSSBreakpoint)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  flex-shrink: 0;

  ${mediaQueryUp.tablet} {
    width: auto;
    padding-left: ${rem(12)};
    justify-content: flex-end;
    margin-left: auto;
  }

  > * + * {
    margin-left: ${rem(12)};
  }
`

const PriceSummary = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;

  ${mediaQueryUp.tablet} {
    text-align: right;
  }
`

const StyledNavWrapper = styled(NavWrapper)`
  &.showInstalments {
    height: ${rem(72)};
  }
`

interface Props {
  offer: App.Offer;
  onViewPackageOptions: () => void;
  calendarsLoading?: boolean;
  cheapestSelectedOption?: OfferPackageOptionView;
  cheapestOption: OfferPackageOptionView;
  className?: string;
  checkIn: moment.Moment;
  instalmentsABTestEnabled: boolean;
  defaultDepositAmountPercentage: number;
  shareModalConfig?: ReturnType<typeof getShareModalConfig>
}

function FlashHotelStickyHeader(props: PropsWithChildren<Props>) {
  const {
    offer,
    onViewPackageOptions,
    children,
    cheapestSelectedOption,
    cheapestOption,
    calendarsLoading,
    className,
    checkIn,
    instalmentsABTestEnabled,
    defaultDepositAmountPercentage,
    shareModalConfig,
  } = props

  const availableForPurchase = useIsAvailableForPurchase()
  const dispatch = useOfferPageDispatch()

  const onViewOptionsClick = () => {
    dispatch({ type: OfferPageActions.closeFilter })
    onViewPackageOptions()
  }

  const flightPrice = useMemo(() => {
    if (offer.bundledWithFlightsOnly) {
      return (cheapestSelectedOption?.flightPrice ?? cheapestOption.flightPrice)
    }
    return 0
  }, [offer, cheapestSelectedOption, cheapestOption])

  const { currentCurrency, showTaxExclusivePrices } = useContext(GeoContext)

  const propertyFee = useMemo(() => {
    return showTaxExclusivePrices ? 0 :
        (cheapestSelectedOption?.propertyFees ?? cheapestOption.propertyFees ?? 0)
  }, [cheapestSelectedOption, cheapestOption, showTaxExclusivePrices])

  const saleUnit = offer.bundledWithFlightsOnly ? 'package*' : offer.saleUnit
  const hidePrices = offer.bundledWithFlightsOnly && !(cheapestSelectedOption?.flightPrice ?? cheapestOption.flightPrice)

  let hotelPrice = cheapestOption.hotelPrice
  if (cheapestSelectedOption) {
    hotelPrice = cheapestSelectedOption.hotelPrice
  }

  const depositAmountPercentage = getDepositAmountPercentage(offer, defaultDepositAmountPercentage)
  const instalmentWeeklyAmount = getWeeklyInstalmentAmount(hotelPrice, checkIn, offer, defaultDepositAmountPercentage)
  const showPayInInstalments = instalmentsABTestEnabled && isInstalmentsAvailableOnOffer(offer, currentCurrency, checkIn) && !!instalmentWeeklyAmount
  const canParticipateInSaveButtonExperiment = offer.type === 'hotel' && offer.property?.isUltraLux !== true
  const isSaveButtonExperimentEnabled = !!useOptimizelyExperiment(OptimizelyClientSideExperiments.tripOfferPageSaveButton, canParticipateInSaveButtonExperiment)
  return (
    <StyledNavWrapper className={`${className} ${cx({ showInstalments: showPayInInstalments })}`}>
      <Root>
        {children}
        <WalledContent enforced={offer.walledGarden} wall={<GenericLoginSignupWall />}>
          {isSaveButtonExperimentEnabled && (<TripGuard>
            <CSSBreakpoint min="tablet">
              <Group direction="horizontal" gap={12} verticalAlign="center">
                {shareModalConfig && (<ShareModal shareConfig={shareModalConfig}>
                  <AssistChip aria-label="Share" variant="filled" size="small" startIcon={<LineShareAltIcon/>}>
                    Share
                  </AssistChip>
                </ShareModal>
                )}
                <BookmarkButton offer={offer} />
              </Group>
            </CSSBreakpoint>
          </TripGuard>
          )}
          <PriceSummarySection max={isSaveButtonExperimentEnabled ? 'mobile' : undefined}>
            <PriceSummary>
              <LoadingIndicator visible={calendarsLoading} floating opaque />
              {!availableForPurchase && !config.LERE_EXPIRED_DEALS_V2_ENABLED && <BodyText variant="large" weight="bold">
                This offer has<CSSBreakpoint as="br" max="mobile" /> SOLD OUT
              </BodyText>}
              {availableForPurchase && !hidePrices && <>
                <OfferFromDurationLabel variant="small" wrap="no-wrap" />
                <div>
                  <Heading as="span" variant="heading6">
                    <FormatCurrency value={hotelPrice + flightPrice + propertyFee} />
                  </Heading>
                  <Caption variant="medium" as="span">/{saleUnit}</Caption>
                </div>
              </>}
              {showPayInInstalments && <SecureWithFlexiblePaymentsPriceDetails currency={currentCurrency} isOfferSummary depositPercentage={depositAmountPercentage} instalmentAmount={instalmentWeeklyAmount} />}
            </PriceSummary>
            {!availableForPurchase && !config.LERE_EXPIRED_DEALS_V2_ENABLED && <TextButton kind="primary" to={`/${OFFER_TYPE_HOTEL_SLUG}`}>
              Browse all hotel offers
            </TextButton>}
            {availableForPurchase &&
              <TextButton kind="primary" onClick={onViewOptionsClick}>
                <CSSBreakpoint max="tablet" as="span">
                  View options
                </CSSBreakpoint>
                <CSSBreakpoint min="desktop" as="span">
                  View package options
                </CSSBreakpoint>
              </TextButton>
              }
          </PriceSummarySection>
        </WalledContent>
      </Root>
    </StyledNavWrapper>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  cheapestSelectedOption: getCheapestSelectedPackageOption(appState, state),
  cheapestOption: getCheapestAvailablePackageOption(state, appState),
  calendarsLoading: areCalendarsLoading(appState, state),
  checkIn: state.checkInDate,
  instalmentsABTestEnabled: getInstalmentsAbTestEnabled(appState),
  defaultDepositAmountPercentage: getDefaultDepositAmountPercentage(appState),
}))(FlashHotelStickyHeader)
